/**
 * Measure an image and apply its cover-fit — writing only if the upload
 * attempt is still current AFTER the measurement await.
 *
 * The measurement is an await sitting between the generation-token checkpoint
 * and the first transform write. Checking the token before it and writing
 * unconditionally after it left a window: a Remove or a second pick landing
 * inside the decode got its bgX/bgY/scale overwritten by the stale file's
 * crop, and the stale run only noticed at the NEXT checkpoint — after the
 * damage (#567, R1 follow-up).
 *
 * A failed measurement is not a stale attempt: the legacy coverFit(0, 0)
 * fallback is preserved, gated on the same currency check.
 *
 * Returns whether the attempt was still current; callers bail on false.
 */
type Dims = {width: number; height: number};

export async function measuredCoverFit({
  dataUrl,
  measure,
  isCurrent,
  coverFit,
}: {
  dataUrl: string;
  measure: (dataUrl: string) => Promise<Dims>;
  isCurrent: () => boolean;
  coverFit: (width: number, height: number) => void;
}): Promise<boolean> {
  let dims: Dims | null = null;
  try {
    dims = await measure(dataUrl);
  } catch {
    dims = null;
  }
  if (!isCurrent()) return false;
  coverFit(dims?.width ?? 0, dims?.height ?? 0);
  return true;
}
